import { Component, Input, ViewChild, OnInit } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capawesome-team/capacitor-file-opener';
import { PermissionManager } from '../permission-manager/permission-manager.component';
import { AlertService } from 'src/app/providers/alert/alert.service';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { DownloadAbortedByUserError, RequestProviderService } from 'src/app/providers/request-provider/request-provider.service';
import { firstValueFrom, Subject } from 'rxjs';
import { HttpResponse } from '@angular/common/http';

/**
 * Component responsible for managing file downloads, displaying progress, and handling file actions such as opening or deleting files.
 *
 * ### Available modes:
 * The `file-downloader` component distinguishes between the following 3 download modes:
 * 
 * - **`"IndirectDownload"`:** On native platforms, it displays a menu with options to download, open, or delete files. On web platforms, the file is downloaded directly.
 * Please make sure that the file can be downloaded without any authentication.
 * Please use a seperate `FileDownloader` instance in this mode for each file that should be downloaded since it contains UI elements.
 * - **`"DirectDownload"`:** On native platforms, the file is directly downloaded.
 * On web platforms, the browser decides whether to display or download the file.
 * Please make sure that the file can be downloaded without any authentication.
 * Please use a seperate `FileDownloader` instance in this mode for each file that should be downloaded since it contains UI elements.
 * - **`"AnswersheetPDFReportDownload"`:** This mode can be used to download PDF reports of a specific answersheet by using the `RequestProvider`.
 * In this mode different PDF answersheet reports can be downloaded by using one `FileDownloader` instance since it contains no UI elements for the specific files.
 * 
 * ### Input Parameters:
 * - **downloadMode (required):**
 *   Valid values are `"IndirectDownload"`, `"DirectDownload"`, and `"AnswersheetPDFReportDownload"` that are described in the "Available modes" section.
 *
 * - **downloadUrl (only required in download mode `"DirectDownload"` or `"IndirectDownload"`):**  
 *   The URL of the file to be downloaded. This parameter is mandatory.
 * 
 * - **openAfterDownload (optional, default: `false`):**  
 *   Determines whether the file should be opened automatically after download. 
 *
 * - **clearFileName (optional and only in download mode `"DirectDownload"` or `"IndirectDownload"`, default: `null`):**  
 *   The custom name to use for the downloaded file. If not specified, the original file name will be used.
 * 
 * - **faIcon (optional and only in download mode `"DirectDownload"` or `"IndirectDownload"`, default: `null`):**  
 *   FontAwesome icon definition to display alongside the download button. If not specified, no icon is displayed.
 */
@Component({
    selector: 'file-downloader',
    templateUrl: './file-downloader.component.html',
    styleUrls: ['./file-downloader.component.scss'],
    standalone: false
})
export class FileDownloader implements OnInit {
  /**
   * Is required to determine whether UI elements should be displayed and how to initialize the `FileDownloader` component.
   * UI elements are only visible, if the download mode is `"DirectDownload"` or `"IndirectDownload"`.
   */
  @Input() downloadMode: DownloadMode;

  /**
   * URL of the file to be downloaded.
   * This attribute will be ignored if the downloadMode is `"AnswersheetPDFReportDownload"`.
   */
  @Input() downloadUrl?: string = null;

  /** Whether to open the file automatically after downloading. Default is `false` */
  @Input() openAfterDownload: boolean = false;

  /** 
   * Optional clear file name to use for the downloaded file.
   * If null, the original file name will be used.
   * This attribute will be ignored if the downloadMode is `"AnswersheetPDFReportDownload"`.
   */
  @Input() clearFileName?: string = null;

  /**
   * FontAwesome icon definition to display in hyperlink mode.
   * This attribute will be ignored if the downloadMode is `"AnswersheetPDFReportDownload"`.
   */
  @Input() faIcon?: IconDefinition = null;

  /** Reference to the permission manager component for handling required storage permissions. */
  @ViewChild("permissionOnboardingRef") permissionManager: PermissionManager;

  /** Tracks the download status. `true` if a file is being downloaded. */
  isDownloading: boolean = false;

  /** Indicates whether the app is running on a native platform (iOS or Android). */
  isNative = Capacitor.isNativePlatform();

  /** The menu icon to be displayed in the UI depending on the platform. */
  menuIcon: string;

  /** Tracks the state of the options menu (open or closed). */
  menuIsOpen: boolean = false;

  /** Array of available options for the action menu. */
  menuOptions: any[] = [];

  /** Message indicating the current download progress. */
  loadingMessage: string = "";

  /**
   * File name of the downloaded file that will be saved on native platforms in the `Directory.Documents` directory.
   * You can find more information about the directory here: https://ionicframework.com/docs/native/filesystem#directory
  */
  private fileName: string;

  /** Subject used only in the `AnswersheetPDFReportDownload` mode to signal the abortion of a PDF report download. */
  private pdfReportDownloadAbortSubject?: Subject<void>;

  /** An optional AbortController instance used to abort ongoing non-PDF report file downloads. */
  private fileDownloadAbortController?: AbortController;

  constructor(
    private alertService: AlertService,
    private translateService: TranslateService,
    private requestProvider: RequestProviderService,
  ) {
    // Since these functions are passed as a callback function,
    // the context of the this reference it contains must be the FileDownloader instance.
    this.abortDownload = this.abortDownload.bind(this);
  }

  /**
   * Initializes the component, sets the appropriate menu icon, and configures native file download progress handling.
   */
  async ngOnInit(): Promise<void> {
    if (this.isNative) {
      this.initFilePath();
    }
    this.setMenuIcon();
  }

  /**
   * Handles menu button clicks, opening the menu on native platforms or triggering a file download on web platforms via XHR request.
   */
  menuButtonClicked(): void {
    if (this.isNative) {
      this.openMenu();
    } else {
      this.downloadFile();
    }
  }

  /**
   * Handles hyperlink clicks, opening the file if it already exists, or downloading it if not.
   */
  async anchorClicked(): Promise<void> {
    if (await this.fileExists()) {
      this.openFile(this.fileName);
    } else {
      this.downloadFile();

      if (!this.openAfterDownload) {
        this.openFile(this.fileName);
      }
    }
  }

  /**
   * Handles the selection of an option from the menu (action sheet), performing actions such as downloading, opening, or deleting a file.
   * 
   * @param event Event containing details about the selected menu option.
   */
  handleSelectedMenuOption(event): void {
    this.menuIsOpen = false;

    switch (event.detail?.data?.action) {
      case "download": {
        this.downloadFile();
        break;
      }

      case "open": {
        this.openFile(this.fileName);
        break;
      }

      case "delete": {
        this.deleteFile().then(_ => this.alertService.presentToast("SUCCESS.SUCCESS_MEDIA_DELETED"));
        break;
      }
    }
  }

  /** Aborts an ongoing download (PDF answersheet report or lesson media files) in a web browser or on native platforms. */
  async abortDownload(): Promise<void> {
    this.hideDownloadProgress();
    if (this.downloadMode === "AnswersheetPDFReportDownload") {
      this.pdfReportDownloadAbortSubject?.next();
      return;
    }

    this.fileDownloadAbortController?.abort();

    if (!this.isNative) {
      return;
    }

    if (await this.fileExists()) {
      this.deleteFile();
    }
  }

  /**
   * Downloads the PDF answer sheet report based on the platform (native or web).
   * 
   * @param answersheetId The ID of the answersheet to be downloaded.
   * @param title The title to be used for the downloaded file.
   */
  public downloadPDFAnswersheetReport(answersheetId: number, title: string) {
    if (this.isNative) {
      this.downloadPDFAnswersheetReportNative(answersheetId, title);
    } else {
      this.downloadPDFAnswersheetReportWeb(answersheetId, title);
    }
  }

  /**
   * Downloads a file on all platforms (web and native).
   * Please make sure that the download of that file doesn't need any authentication!
   */
  private downloadFile(): void {
    if (this.isNative) {
      this.downloadFileNative();
    } else {
      this.downloadFileWeb();
    }
  }

  /**
   * Downloads the file on a native platform using the Fetch API and streams the response.
   * The file is saved in 10 MB chunks using Capacitor's Filesystem.appendFile during the download process.
   * This approach reduces memory usage and saves time by writing chunks as they are received.
   */
  private async downloadFileNative(): Promise<void> {
    const { storage } = await this.permissionManager.checkAndMayAskForNativePermissions({ singlePermissions: ["storage"] });

    if (!storage) {
      this.alertService.showError("ERROR.ERROR", "ERROR.ERROR_MISSING_FILESYSTEM_PERMISSION");
      return;
    }

    this.showDownloadProgress();
    this.setProgressLoadingMessage(0);

    this.fileDownloadAbortController = new AbortController();

    try {
      const response = await fetch(this.downloadUrl, {
        method: "GET",
        signal: this.fileDownloadAbortController.signal
      });

      if (!response.ok || !response.body) {
        this.alertService.showError("ERROR.ERROR", "ERROR.ERROR_DOWNLOAD");
        this.hideDownloadProgress();
        return;
      }

      const reader = response.body.getReader();
      const CHUNK_SIZE = 10_000_000; // 10 MB
      let receivedLength = 0;
      let chunks: Uint8Array[] = [];
      let totalLength = +response.headers.get('Content-Length') || 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        receivedLength += value.length;

        // If we've reached or exceeded the chunk size, write to file
        let currentLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
        while (currentLength >= CHUNK_SIZE) {
          // Concatenate enough chunks to reach CHUNK_SIZE
          let chunkToWrite = new Uint8Array(CHUNK_SIZE);
          let offset = 0;
          while (offset < CHUNK_SIZE && chunks.length > 0) {
            let chunk = chunks[0];
            let copyLength = Math.min(chunk.length, CHUNK_SIZE - offset);
            chunkToWrite.set(chunk.subarray(0, copyLength), offset);
            offset += copyLength;
            if (copyLength < chunk.length) {
              chunks[0] = chunk.subarray(copyLength);
            } else {
              chunks.shift();
            }
          }
          const base64Chunk = await this.arrayBufferToBase64String(chunkToWrite.buffer);
          await Filesystem.appendFile({
            path: this.fileName,
            data: base64Chunk,
            directory: Directory.Documents
          });
          currentLength -= CHUNK_SIZE;
        }

        if (totalLength) {
          this.setProgressLoadingMessage(receivedLength / totalLength);
        }
      }

      // Write any remaining data
      if (chunks.length > 0) {
        let remainingLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
        let remaining = new Uint8Array(remainingLength);
        let offset = 0;
        for (let chunk of chunks) {
          remaining.set(chunk, offset);
          offset += chunk.length;
        }
        const base64Chunk = this.arrayBufferToBase64String(remaining.buffer);
        await Filesystem.appendFile({
          path: this.fileName,
          data: base64Chunk,
          directory: Directory.Documents
        });
        this.setProgressLoadingMessage(1);
      }

      this.hideDownloadProgress();
      this.alertService.presentToast("SUCCESS.SUCCESS_MEDIA_DOWNLOADED");
      this.setMenuIcon();
      if (this.openAfterDownload) {
        this.openFile(this.fileName);
      }
    } catch (error) {
      this.hideDownloadProgress();
      if (error?.name === "AbortError") {
        this.alertService.presentToast("SUCCESS.SUCCESS_MEDIA_DOWNLOAD_ABORTED");
      } else {
        this.alertService.showError("ERROR.ERROR", "ERROR.ERROR_DOWNLOAD");
      }
    }
  }

  /**
   * Downloads the file on web platforms using the Fetch API.
   */
  private async downloadFileWeb(): Promise<void> {
    this.showDownloadProgress();
    this.setProgressLoadingMessage(0);

    try {
      this.fileDownloadAbortController = new AbortController();

      const response = await fetch(this.downloadUrl, {
        method: "GET",
        signal: this.fileDownloadAbortController.signal
      });

      if (!response.ok || !response.body) {
        this.alertService.showError("ERROR.ERROR", "ERROR.ERROR_DOWNLOAD");
        return;
      }

      const totalLength = +response.headers.get('Content-Length');

      const reader = response.body.getReader();
      let receivedLength = 0;
      const chunks = [];

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        chunks.push(value);
        receivedLength += value.length;
        this.setProgressLoadingMessage(receivedLength / totalLength);
      }

      const blob = new Blob(chunks);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = this.clearFileName;
      link.click();
    } catch (error) {
      if (error?.name === "AbortError") {
        this.alertService.presentToast("SUCCESS.SUCCESS_MEDIA_DOWNLOAD_ABORTED");
      } else {
        this.alertService.showError("ERROR.ERROR", "ERROR.ERROR_DOWNLOAD");
      }
    } finally {
      this.hideDownloadProgress();
    }
  }

  /**
   * Initiates the download of the PDF answer sheet report on native platforms.
   * This method checks for the required storage permissions before attempting 
   * to download the report. If permissions are granted, it retrieves the report 
   * data and saves it to the device's file system.
   * 
   * @param answersheetId The ID of the answersheet to be downloaded.
   * @param title The title to be used for the downloaded PDF file.
   */
  private async downloadPDFAnswersheetReportNative(answersheetId: number, title: string): Promise<void> {
    const { storage } = await this.permissionManager.checkAndMayAskForNativePermissions({ singlePermissions: ["storage"] });

    if (!storage) {
      this.alertService.showError("ERROR.ERROR", "ERROR.ERROR_MISSING_FILESYSTEM_PERMISSION");
      return;
    }

    this.showDownloadProgress();
    this.loadingMessage = this.translateService.instant("GENERAL.PLEASE_WAIT");

    try {
      const httpResponseWithPDF = await this.downloadPDFReport(answersheetId);
      const pdfBase64Data = this.arrayBufferToBase64String(httpResponseWithPDF["body"]);

      const filePath = this.sanitizeFilename(`Report_${title}.pdf`);
      await Filesystem.writeFile({
        path: filePath,
        data: pdfBase64Data,
        directory: Directory.Documents,
        recursive: true
      });

      this.hideDownloadProgress();

      if (this.openAfterDownload) {
        this.openFile(filePath);
      }
    } catch (e) {
      if (e instanceof DownloadAbortedByUserError) {
        this.alertService.presentToast("SUCCESS.SUCCESS_MEDIA_DOWNLOAD_ABORTED");
      } else {
        this.hideDownloadProgress();
        this.alertService.showError("ERROR.ERROR", "ERROR.ERROR_DOWNLOAD");
      }
    }
  }

  /**
   * Initiates the download of the PDF answer sheet report on web platforms.
   * It displays a loading indicator during the download process.
   * 
   * @param answersheetId The ID of the answersheet to be downloaded.
   * @param title The title to be used for the downloaded PDF file.
   */
  private async downloadPDFAnswersheetReportWeb(answersheetId: number, title: string) {
    try {
      this.showDownloadProgress();
      this.loadingMessage = this.translateService.instant("GENERAL.PLEASE_WAIT");
      const httpResponseWithPDF = await this.downloadPDFReport(answersheetId);
      const binaryData = [];
      binaryData.push(httpResponseWithPDF["body"]);
      const downloadLink = document.createElement("a");
      const pdf = new Blob(binaryData, { type: "application/pdf" })
      downloadLink.href = window.URL.createObjectURL(pdf);
      const filename = this.sanitizeFilename(`Report_${title}.pdf`);
      downloadLink.setAttribute("download", filename);
      document.body.appendChild(downloadLink);
      this.hideDownloadProgress();
      downloadLink.click();
    } catch (error) {
      this.hideDownloadProgress();
      if (error instanceof DownloadAbortedByUserError) {
        this.alertService.presentToast("SUCCESS.SUCCESS_MEDIA_DOWNLOAD_ABORTED");
      } else {
        this.alertService.showError("ERROR.ERROR", "ERROR.ERROR_DOWNLOAD");
      }
    }
  }

  /**
   * Sends a request to download the PDF report for the specified answersheet.
   * The request includes the answersheet ID and the user's timezone and is
   * performed by the requestProvider service.
   * 
   * @param answersheetId The ID of the answersheet for which the report is requested.
   * @returns A promise that resolves to the HTTP response containing the PDF data as an ArrayBuffer.
   */
  private async downloadPDFReport(answersheetId: number): Promise<HttpResponse<ArrayBuffer>> {
    this.pdfReportDownloadAbortSubject = new Subject();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const httpRequestObservable = this.requestProvider.getAnswersheetReport(answersheetId, timezone, this.pdfReportDownloadAbortSubject);
    const httpResponsePromise = await firstValueFrom(httpRequestObservable);
    return httpResponsePromise;
  }

  /**
   * Converts an ArrayBuffer to a Base64 encoded string.
   * A Base64 encoded string is required for the Capacitor Filesystem plugin to store the data,
   * as binary data must be in Base64 format.
   * 
   * @param arrayBuffer The ArrayBuffer to be converted.
   * @returns A Base64 encoded string representation of the provided ArrayBuffer.
   */
  private arrayBufferToBase64String(arrayBuffer: ArrayBuffer): string {
    let binaryData = "";
    const bytes = new Uint8Array(arrayBuffer);
    bytes.forEach(data => {
      binaryData += String.fromCharCode(data);
    });
    return btoa(binaryData);
  }

  /**
   * Opens the downloaded file using the Capacitor FileOpener plugin on native platforms.
   * This function can only be used on native platforms!
   */
  private async openFile(path: string): Promise<void> {
    const fileStats = await Filesystem.stat({
      path: path,
      directory: Directory.Documents
    });
    FileOpener.openFile(
      {
        path: fileStats.uri
      }
    ).catch(_ => {
      this.alertService.presentToast("ERROR.ERROR_NO_APP_TO_OPEN_FILE");
    });
  }

  /**
   * Deletes the downloaded file from native storage using its file name.
   */
  private async deleteFile(): Promise<void> {
    await Filesystem.deleteFile({
      path: this.fileName,
      directory: Directory.Documents
    });
    this.setMenuIcon();
  }

  /**
   * Opens the action menu, displaying options such as downloading, opening, or deleting the file.
   * This function can only be used on native platforms!
   */
  private async openMenu(): Promise<void> {
    this.fileExists();
    await this.determineActionSheetOptions();
    this.menuIsOpen = true;
  }

  /**
   * Shows a cancelable modal loading dialog, if one of the following conditions is met:
   * - The platform is native (iOS or Android)
   * - The download mode is set to `AnswersheetPDFReportDownload`
   * 
   * Shows the download progress within an embeddable `file-downloader` HTML component, if:
   * - The platform is web and the download mode is set to `IndirectDownload`
   */
  private showDownloadProgress(): void {
    this.isDownloading = true;
    this.loadingMessage = "";
  }

  /** Hides an opened modal loading dialog or the elements within the `file-downloader` HTML component during a download. */
  private hideDownloadProgress(): void {
    this.isDownloading = false;
  }

  /**
   * Determines the available options for the action menu based on whether the file already exists.
   * If the file exists, a file removal, open file and cancel option will be displayed.
   * Otherwise a donwload and cancel option will be displayed.
   * This function can only be used on native platforms!
   */
  private async determineActionSheetOptions(): Promise<void> {
    const actionSheetOptions = [];

    if (await this.fileExists()) {
      actionSheetOptions.push({
        text: this.translateService.instant("LESSON.MEDIA_DOWNLOAD_OPEN_FILE"),
        data: {
          action: 'open',
        },
      });

      actionSheetOptions.push({
        text: this.translateService.instant("LESSON.MEDIA_DOWNLOAD_DELETE_FILE"),
        role: 'destructive',
        data: {
          action: 'delete',
        },
      });
    } else {
      actionSheetOptions.push({
        text: this.translateService.instant("LESSON.MEDIA_DOWNLOAD_START"),
        data: {
          action: 'download',
        },
      });
    }

    actionSheetOptions.push({
      text: this.translateService.instant("BUTTONS.CANCEL"),
      role: 'cancel',
      data: {
        action: 'cancel',
      }
    })

    this.menuOptions = actionSheetOptions;
  }

  /**
   * Sets the menu icon based on the platform (web, Android, iOS).
   */
  private async setMenuIcon(): Promise<void> {
    if (this.downloadMode === "AnswersheetPDFReportDownload") {
      return;
    }

    if (!this.isNative) {
      this.menuIcon = "arrow-down-circle-outline";
    } else if (await this.fileExists()) {
      this.menuIcon = "checkmark-circle";
    } else {
      this.menuIcon = "arrow-down-circle-outline";
    }
  }

  /**
   * Checks whether the file already exists in the filesystem of the native platform.
   * This function can only be used on native platforms and only checks for existence
   * if the downloadMode is `"DirectDownload"` or `"IndirectDownload"`.
   * If the downloadMode is `"AnswersheetPDFReportDownload"` always `false` will be returned.
   * 
   * @returns Promise that resolves to true if the file exists, false otherwise.
   */
  private async fileExists(): Promise<boolean> {
    if ((this.downloadMode === "DirectDownload" || this.downloadMode === "IndirectDownload") && this.fileName) {
      return await Filesystem.stat({
        path: this.fileName,
        directory: Directory.Documents
      })
        .then(_ => {
          return true;
        })
        .catch(_ => {
          return false;
        });
    } else {
      return false;
    }
  }

  /**
   * Sets the file path based on the provided download URL and file name.
   * If the file has the clear file name `awesomeFile.mp4` and the hashed file name `9f327416c325a0052ad856.mp4` the filename will be concatenated with the first eight characters of the hashed file name to ensure human readable and unique filenames.
   * The result of these two file names would be `awesomeFile_9f327416.mp4`.
   * If the clear file name is nullish or an empty string, only the hashed file name will be selected as filename.
   */
  private initFilePath(): void {
    if (!this.downloadUrl || this.downloadMode === "AnswersheetPDFReportDownload") {
      return;
    }

    const lastSlashIndex = this.downloadUrl.lastIndexOf("/");
    if (lastSlashIndex === -1) {
      // URL invalid -> Makes the component invisible
      this.downloadUrl = null;
      return;
    }

    // "http://example.com/path1/path2/9f327416c325a0052ad856.mp4" -> hashedFileName = "9f327416c325a0052ad856.mp4"
    const hashedFileName = this.downloadUrl.slice(lastSlashIndex + 1);

    if (!this.clearFileName) {
      this.fileName = hashedFileName;
      return;
    }

    // "9f327416c325a0052ad856.mp4" -> fileExtension = ".mp4"
    const fileExtensionIndex = hashedFileName.lastIndexOf(".");
    let fileExtension = "";
    if (fileExtensionIndex !== -1) {
      fileExtension = hashedFileName.slice(fileExtensionIndex);
    }

    // "awesomeFile.mp4" -> clearFileNameWithoutExtension = "awesomeFile"
    let clearFileNameExtensionIndex = this.clearFileName.lastIndexOf(".");
    let clearFileNameWithoutExtension = this.clearFileName;
    if (clearFileNameExtensionIndex !== -1) {
      clearFileNameWithoutExtension = this.clearFileName.slice(0, clearFileNameExtensionIndex);
    }

    // "9f327416c325a0052ad856.mp4" -> first8CharacterOfHash = "9f327416"
    const first8CharacterOfHash = hashedFileName.slice(0, 8);

    this.fileName = this.sanitizeFilename(`${clearFileNameWithoutExtension}_${first8CharacterOfHash}${fileExtension}`);
  }

  /**
   * Sanitizes a given filename by removing reserved characters and ensuring it 
   * does not exceed a maximum length of 100 characters. Preserves the file 
   * extension if present.
   *
   * - Reserved characters removed: `|`, `\`, `?`, `*`, `<`, `"`, `:`, `>`, `+`, `[`, `]`, `/`, `'`.
   * - If the filename exceeds the maximum length, it truncates the name while retaining the extension.
   * - If no file extension is present, truncates the filename directly.
   *
   * @param filename - The input filename to sanitize.
   * @returns A sanitized version of the filename that is safe for use.
   */
  private sanitizeFilename(filename: string): string {
    const maxFilenameLength = 100;
    const reservedCharacters = ["|", "\\", "?", "*", "<", "\"", ":", ">", "+", "[", "]", "/", "'"];

    let safeFilename = filename;
    reservedCharacters.forEach(reservedChar => {
      safeFilename = safeFilename.replace(reservedChar, "");
    });

    const dotOfFileExtensionPos = safeFilename.lastIndexOf(".");

    if (dotOfFileExtensionPos !== -1) {
      // filename contains a file extension
      const fileExtension = safeFilename.slice(dotOfFileExtensionPos);
      const limitedFilenameWithoutExtension = safeFilename
        .slice(0, dotOfFileExtensionPos) // remove file extension
        .slice(0, maxFilenameLength - fileExtension.length); // limit filename limit
      // add file extension to the limited filename
      return limitedFilenameWithoutExtension + fileExtension;
    } else {
      // filename doesn't contain a file extension
      return safeFilename.slice(0, maxFilenameLength);
    }
  }

  /**
   * Updates the loading message to reflect the current download progress.
   * The message is localized using the translation service.
   *
   * @param downloadProgress - The current decimal download progress (ex. `0.79`).
   */
  private setProgressLoadingMessage(downloadProgress: number) {
    const progress = (downloadProgress * 100).toFixed(0);
    this.loadingMessage = `${this.translateService.instant('LESSON.MEDIA_DOWNLOAD')} - ${progress} %`;
  }
}

/**
 * Defines the download modes for the `file-downloader` component.
 *
 * - **`"IndirectDownload"`:** Displays a menu for file actions on native platforms; downloads directly on web.
 * - **`"DirectDownload"`:** Direct download on native platforms; browser-dependent on web.
 * - **`"AnswersheetPDFReportDownload"`:** Downloads PDF reports of a specific answersheet using the `RequestProvider`, without UI elements.
 */
type DownloadMode = "DirectDownload" | "IndirectDownload" | "AnswersheetPDFReportDownload";